import {Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy, HostListener} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {DataService} from './shared/services/data.service';
import {AuthService} from './shared/services/auth.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {RolesEnum} from './shared/enums/roles.enum';
import {ThemeService} from './shared/services/theme.service';
import {Observable, Subscription} from 'rxjs';
import {Option} from './shared/models/option.model';
import packageInfo from '../../package.json';
import {OrderService} from './shared/services/order.service';
import {OrganizationService} from "./shared/services/organization.service";
import {environment} from "../environments/environment";
import {SubcontractorService} from "./shared/services/subcontractor.service";
import {Organization} from "./shared/models/organization.model";
import {PlanEnum} from "./shared/enums/plan.enum";
import {CustomerService} from "./shared/services/customer.service";
import {Customer} from "./shared/models/customer.model";
import {QuotationService} from "./shared/services/quotation.service";
import {Order} from "./shared/models/order.model";
import {Quotation} from "./shared/models/quotation.model";
import {ShipmentService} from "./shared/services/shipment.service";
import moment from 'moment-business-days';
import {PluginEnum} from "./shared/enums/plugin.enum";
import {DateHelper} from "./shared/helpers/date.helper";
import {ProfileComponent} from './shared/components/profile/profile.component';
import {PrintoclockService} from "./shared/services/printoclock.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly environment = environment;
  private translate = inject(TranslateService);
  private responsive = inject(BreakpointObserver);
  private themeService = inject(ThemeService);
  private orderService = inject(OrderService);
  private quotationService = inject(QuotationService);
  dataService = inject(DataService);
  router = inject(Router);
  dialog = inject(MatDialog);
  authService = inject(AuthService);
  shipmentService = inject(ShipmentService);
  subcontractorService = inject(SubcontractorService);
  private subscriptions: Subscription[];
  options$: Observable<Array<Option>> = this.themeService.getThemeOptions();
  smallLayout = false;
  version: string = packageInfo.version;
  menuOpened = true;
  RolesEnum = RolesEnum;
  searchedValue = '';
  organizationName = '';
  organizationService = inject(OrganizationService);
  organizationNames: Record<string, string> = {};
  userOrganizations: Organization[] = [];
  protected readonly PlanEnum = PlanEnum;
  customers: Customer[] = [];
  customerService = inject(CustomerService);
  orders: Order[] = [];
  quotations: Quotation[] = [];
  searchOpened = false;
  searchFocused = false;
  importProgress = false;
  mobileSearchActive = false;
  protected readonly PluginEnum = PluginEnum;
  isSupplierOf = false;
  private printoclockService = inject(PrintoclockService);
  private route = inject(ActivatedRoute);
  queryParams: { accessMode: 'user' | 'customer' | 'storeManager' | 'supplier' | '', shopCustomerId: string };
  private visualViewportListener?: () => void;

  ngOnInit(): void {
    this.updateViewportHeight();
    this.registerViewportListeners();
    requestAnimationFrame(() => this.updateViewportHeight());
    setTimeout(() => this.updateViewportHeight(), 150);
    this.menuOpened = !this.themeService.smallLayout;
    registerLocaleData(localeFr, 'fr');
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');
    this.initMoment();
    this.subscriptions = [
      this.responsive.observe(Breakpoints.XSmall)
        .subscribe(result => {
          this.smallLayout = result.matches;
        }),
      this.router.events.subscribe(evt => {
        if (!(evt instanceof NavigationEnd)) {
          return;
        }
        window.scrollTo(0, 0);
      }),
      this.organizationService.organization$.subscribe(organization => {
        if (!organization?.id) {
          this.organizationName = '';
          return
        }
        this.customerService
          .userCustomersAccounts(organization.id,
            this.authService.currentUser.customerIds).then(customers => {
          this.customers = customers;
        });
        this.subcontractorService.getByOrgId(organization.id)
          .then(sub => this.organizationName = sub.name);
        Promise.all([
          this.organizationService.listUserOrganizations(this.authService.getUid),
          this.organizationService.listAdminOrganizations(this.authService.getUid)
        ]).then(async ([userCompanies, adminCompanies]) => {
          this.isSupplierOf = !!userCompanies.find(org => org.supplierOfOrgIds.includes(organization.id)) ||
            !!adminCompanies.find(org => org.supplierOfOrgIds.includes(organization.id));
        });
      })
    ];
    const savedTheme = localStorage.getItem('theme') || 'slate-indigo';
    this.themeService.setTheme(savedTheme);
    this.authService.auth.onAuthStateChanged(user => {
      if (!user || user.isAnonymous) {
        this.organizationNames = {};
        this.userOrganizations = [];
        return;
      }
      this.loadUserOrganisations();
    });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.searchOpened = false;
      }
    });
    this.route.queryParams.pipe().subscribe(params => {
      this.queryParams = params as
        { accessMode: 'user' | 'customer' | 'storeManager' | 'supplier' | '', shopCustomerId: string };
    });
  }

  ngOnDestroy(): void {
    this.unregisterViewportListeners();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Registers iOS-friendly visual viewport listeners to keep app height in sync.
   */
  private registerViewportListeners(): void {
    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      return;
    }

    this.visualViewportListener = () => this.updateViewportHeight();
    visualViewport.addEventListener('resize', this.visualViewportListener);
    visualViewport.addEventListener('scroll', this.visualViewportListener);
  }

  /**
   * Cleans up visual viewport listeners.
   */
  private unregisterViewportListeners(): void {
    const visualViewport = window.visualViewport;
    if (!visualViewport || !this.visualViewportListener) {
      return;
    }

    visualViewport.removeEventListener('resize', this.visualViewportListener);
    visualViewport.removeEventListener('scroll', this.visualViewportListener);
  }

  /**
   * Handles theme changes by updating the application theme and storing the selected theme in localStorage.
   * @param themeToSet - The theme to be applied and saved.
   */
  themeChangeHandler(themeToSet: string): void {
    this.themeService.setTheme(themeToSet);
    const currentTheme = localStorage.getItem('theme');
    if (currentTheme) {
      localStorage.removeItem('theme');
    }
    localStorage.setItem('theme', themeToSet);
  }

  /**
   * Opens the user profile edit dialog/popup.
   */
  openProfileDialog(): void {
    this.dialog.open(ProfileComponent, {
      maxWidth: '500px',
      width: '90vw',
      panelClass: 'premium-dialog-panel'
    });
  }

  /**
   * Performs searches for orders and quotations using the specified transaction ID.
   */
  async find(): Promise<void> {
    if (!this.searchedValue) {
      this.searchOpened = false;
      return;
    }
    this.orders = await this.orderService.searchOrderByTransactionId(this.searchedValue);
    this.quotations = await this.quotationService.searchOrderByErpId(this.searchedValue);
    const shipments = await this.shipmentService
      .searchByCampaignCode(this.organizationService.currentOrganization.id, this.searchedValue);
    if (shipments.length) {
      for (const shipment of shipments) {
        const order = await this.orderService.getOrderById(shipment.orderId);
        if (order) {
          this.orders.push(order);
        }
      }
    }
    const imports = await this.printoclockService.searchByReference(Number(this.searchedValue));
    for (const importOrder of imports) {
      if (importOrder.mpTransactionId) {
        const linked = await this.orderService.searchOrderByTransactionId(importOrder.mpTransactionId);
        for (const order of linked) {
          if (!this.orders.some(o => o.id === order.id)) {
            this.orders.push(order);
          }
        }
      }
    }
    if (!isNaN(Number(this.searchedValue)) && !this.quotations.length && !this.orders.length) {
      this.searchOpened = true;
      return;
    }
    this.searchOpened = !!(this.quotations.length || this.orders.length);
  }

  /**
   * Handles search bar focus events.
   */
  onSearchFocus(): void {
    this.searchFocused = true;
    this.searchOpened = !!(this.orders.length || this.quotations.length);
  }

  /**
   * Handles search bar blur events with a short delay to allow clicks on search results.
   */
  onSearchBlur(): void {
    setTimeout(() => {
      this.searchFocused = false;
    }, 200);
  }

  /**
   * Closes the search results box.
   */
  closeSearch(): void {
    this.searchOpened = false;
    this.searchFocused = false;
  }

  /**
   * Opens the mobile search mode and focuses the search input.
   */
  openMobileSearch(): void {
    this.mobileSearchActive = true;
    setTimeout(() => {
      const searchInput = document.querySelector('.search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

  /**
   * Closes the mobile search mode and clears search state.
   */
  closeMobileSearch(): void {
    this.mobileSearchActive = false;
    this.searchedValue = '';
    this.orders = [];
    this.quotations = [];
    this.searchOpened = false;
  }

  /**
   * Clears the current search input and results.
   */
  clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchedValue = '';
    this.orders = [];
    this.quotations = [];
    this.searchOpened = false;
  }

  /**
   * Updates the CSS viewport height variable used by the root layout.
   */
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  @HostListener('window:pageshow')
  updateViewportHeight(): void {
    const visualViewport = window.visualViewport;
    const viewportHeight = Math.max(
      window.innerHeight,
      visualViewport?.height ?? 0,
      document.documentElement.clientHeight
    );
    document.documentElement.style.setProperty('--okto-app-height', `${Math.round(viewportHeight)}px`);
  }

  /**
   * Global listener for spotlight search hotkeys.
   * Pressing '/' or 'Ctrl+K' / 'Cmd+K' will focus the search bar.
   * Pressing 'Escape' will close search.
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    const isSlash = event.key === '/';
    const isCtrlK = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';

    if (isSlash || isCtrlK) {
      const activeEl = document.activeElement;
      const isInput = activeEl && (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName) || activeEl?.hasAttribute('contenteditable'));
      if (isInput) {
        return;
      }

      event.preventDefault();
      if (this.smallLayout) {
        this.openMobileSearch();
      } else {
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    }

    if (event.key === 'Escape' && (this.searchOpened || this.mobileSearchActive)) {
      this.closeSearch();
      if (this.smallLayout) {
        this.closeMobileSearch();
      }
    }
  }

  goToAction(): void {
    this.menuOpened = false;
  }

  /**
   * Listener for clicks outside the sidebar menu on desktop layout to automatically collapse it.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpened || this.smallLayout || !this.menuDisplayed()) {
      return;
    }

    const target = event.target as HTMLElement;
    const isSidenavClick = target.closest('.okto-sidenav');
    const isToggleClick = target.closest('.menu-toggle-btn');

    if (!isSidenavClick && !isToggleClick) {
      this.menuOpened = false;
    }
  }

  /**
   * Determines if the menu should be displayed based on the user's logged-in status,
   * current route, and their association with the current organization.
   * @returns Returns true if the menu should be displayed, otherwise false.
   */
  menuDisplayed(): boolean {
    return this.authService.isLoggedIn &&
      !this.router.url.includes('auth') &&
      this.router.url !== '/' &&
      (
        this.organizationService.currentOrganization?.users.includes(this.authService.currentUser.id) ||
        this.organizationService.currentOrganization?.administrators.includes(this.authService.currentUser.id)
        || !!this.customers.length || this.isSupplierOf
      );
  }

  /**
   * Load organizations of the connected user
   */
  private loadUserOrganisations(): void {
    Promise.all([
      this.organizationService.listUserOrganizations(this.authService.getUid),
      this.organizationService.listAdminOrganizations(this.authService.getUid)
    ]).then(async ([userOrganizations, organizations]) => {
      const allOrgs = [...userOrganizations, ...organizations];
      await Promise.all(allOrgs.map(org =>
        this.subcontractorService.getByOrgId(org.id).then(sub => [org.id, sub.name]))
      ).then(entries => this.organizationNames = Object.fromEntries(entries));
      this.userOrganizations = allOrgs;
    });
  }

  /**
   * Go to organization dashboard
   * @param organization The selected organization
   * @returns Router navigate a result
   */
  goToOrganization(organization: Organization): Promise<boolean> {
    this.organizationService.currentOrganization = organization;
    return this.router.navigate(['/', organization.oid, 'dashboard']);
  }

  /**
   * Handles the import process by synchronizing orders for the current organization
   * based on the specified search criteria and updates the data view accordingly.
   * Manages the progress state throughout the operation.
   */
  async import(): Promise<void> {
    this.importProgress = true;
    try {
      await this.orderService.syncOrder(this.organizationService.currentOrganization.id, this.searchedValue);
    } catch (error) {
      console.error(error);
    } finally {
      this.importProgress = false;
      await this.find();
    }
  }

  /**
   * Initializes the moment.js library with a custom locale configuration for French regional settings.
   * This includes defining the working weekdays, a list of holidays for specified years,
   * and the holiday date format.
   */
  initMoment(): void {
    const currentYear = new Date().getFullYear();
    moment.updateLocale('fr', {
      workingWeekdays: [1, 2, 3, 4, 5],
      holidays: DateHelper.getFrenchHolidays(currentYear - 1, currentYear + 5),
      holidayFormat: 'YYYY-MM-DD',
    });
  }

  /**
   * Navigates to a common path within the application based on the specified path and current organization.
   * @param path The relative path to navigate to within the current organization's context.
   * @returns A promise that resolves once the navigation is complete.
   */
  openCommonPath(path: string): Promise<boolean> {
    this.goToAction();
    return this.router.navigate([this.organizationService.currentOrganization.oid, path], {
      queryParams: this.queryParams,
    });
  }

  /**
   * Checks if the given path is active in the current route.
   */
  isRouteActive(path: string): boolean {
    return this.router.url.includes('/' + path);
  }
}

